/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import FormSelect from '../../components/forms/form-select';
import { useAccessibleProjects, projectFieldOptions, appIdFromField, defaultProjectId, SHARED_PROJECT } from '../../hooks/useProjectScope';
import PartyEditor from '../../components/forms/party-editor';
import { recipientListsService } from '../../services/recipient-lists.service';
import type { Party } from '../../services/notification-engine.types';
import { extractErrorMessage } from '../../utils/error-handler';

const schema = z.object({
  // Empty means shared; the API honours this only inside the caller's scope.
  // Required: a blank appId is what the API reads as "shared with every
  // project", so an unset field would quietly author a global row.
  appId: z.string().min(1, 'Choose the project this belongs to.'),
  recipientListCode: z.string().min(1, 'List code is required'),
  description: z.string().max(1000).optional().or(z.literal('')),
  isDoNotContact: z.boolean().optional(),
  // A list that resolves to nobody is not a group — referencing one would
  // silently contribute no recipients wherever it appears.
  parties: z.array(z.any()).min(1, 'A list needs at least one party'),
});
type FormData = z.infer<typeof schema>;

const RecipientListForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'recipient list', basePath: '/recipient-lists' });
  const isEdit = Boolean(id);
  const { projects, canScopeByProject } = useAccessibleProjects();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { appId: defaultProjectId(projects), recipientListCode: '', description: '', isDoNotContact: false, parties: [] },
    mode: 'onChange',
  });
  const isDoNotContact = watch('isDoNotContact');
  // A nested reference must be in the same project, or shared.
  const projectId = appIdFromField(watch('appId'));

  const { data: listData, isLoading } = useQuery({
    queryKey: ['recipientList', id],
    queryFn: () => recipientListsService.getById(id!),
    enabled: isEdit && isReady && !!id,
  });

  useEffect(() => {
    if (listData && isEdit) {
      reset({
        appId: listData.appId || SHARED_PROJECT,
        recipientListCode: listData.recipientListCode || '',
        description: listData.description || '',
        isDoNotContact: !!listData.isDoNotContact,
        parties: listData.parties || [],
      });
    }
  }, [listData, isEdit, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEdit && id ? recipientListsService.update(id, data) : recipientListsService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipientLists'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Recipient list ${isEdit ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Save failed'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => mutation.mutate({
    // Create only: the update schema has no appId.
    ...(isEdit ? {} : { appId: appIdFromField(d.appId) }),
    recipientListCode: d.recipientListCode,
    description: d.description || null,
    isDoNotContact: !!d.isDoNotContact,
    parties: d.parties as Party[],
  });

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <div className="p-4" data-testid="SCR-RecipientList-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader icon="pi pi-users" title={isEdit ? 'Edit Recipient List' : 'Create Recipient List'} onBack={() => navigateToList()} />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-5xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {canScopeByProject && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''}
                options={projectFieldOptions(projects, field.value)}
                onChange={field.onChange} disabled={isEdit}
                helperText={isEdit
                  ? 'Set when this was created; the API does not move one afterwards.'
                  : 'Which project owns this. It cannot be moved afterwards.'} />
            )} />
          )}
          <Controller name="recipientListCode" control={control} render={({ field }) => (
            <Input label="List code" required {...field} error={errors.recipientListCode?.message} placeholder="COMPLIANCE_OBSERVERS" disabled={isEdit} />
          )} />
          <Controller name="isDoNotContact" control={control} render={({ field }) => (
            <div className="flex items-start gap-2 sm:pt-6">
              <Checkbox inputId="dnc" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
              <label htmlFor="dnc" className="text-sm text-gray-700 dark:text-gray-300 leading-tight">
                Do-not-contact list
                <span className="block text-xs text-gray-400">Its parties become endpoints to suppress, not an audience.</span>
              </label>
            </div>
          )} />
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
          )} />
        </div>

        {isDoNotContact && (
          <Message severity="warn" className="w-full" text="Checked before every send. Numbers match on the last ten digits and emails ignore case, so how it was typed does not matter." />
        )}

        <div className="ds-card p-5">
          <Controller name="parties" control={control} render={({ field }) => (
            <PartyEditor
              projectId={projectId}
              value={(field.value || []) as Party[]}
              onChange={field.onChange}
              label={isDoNotContact ? 'Suppressed endpoints *' : 'Parties *'}
              hint={isDoNotContact
                ? 'An external party has no account, so this is the only way it can say stop.'
                : 'Each party carries exactly one kind of identifier, decided by its type.'}
              context="list"
              testId="RecipientListForm.Parties"
            />
          )} />
          {errors.parties && <small className="text-red-500">{errors.parties.message as string}</small>}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => navigateToList()} />
          <Button type="submit" label={mutation.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'} icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
        </div>
      </form>
    </div>
  );
};

export default RecipientListForm;
